import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AWS_CONFIG } from '../config/aws';
import PhoneVerificationModal from './PhoneVerificationModal';
import { GoogleLogo, NaverLogo, KakaoLogo } from './icons/SocialLogos';
import { formatKoreanPhone } from '../utils/phone';
import { providerStatus } from '../data/socialProviderStatus';
import ConfirmDialog from './ConfirmDialog';

export default function LoginForm() {
  const { user, signIn, signUp, confirmSignUp, signOut, pendingPhoneVerification, clearPendingPhoneVerification } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', code: '' });
  const [pendingEmailLessSignUp, setPendingEmailLessSignUp] = useState(false);
  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // OAuth provider consoles register only the canonical (non-www) redirect URI.
  // Strip a leading "www." so a user who lands on www.koa-asia.com still sends
  // the registered redirect_uri. The index.html inline script already canonicalizes
  // the URL on first load; this is defense in depth.
  const oauthRedirectUri = () =>
    `${window.location.origin.replace('://www.', '://')}/login`;

  const handleGoogleLogin = () => {
    const redirectUri = oauthRedirectUri();
    const state = 'google_' + Math.random().toString(36).slice(2);
    sessionStorage.setItem('google_state', state);
    const scope = 'openid email profile';
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${AWS_CONFIG.google.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${state}&prompt=select_account`;
  };

  const handleNaverLogin = () => {
    const redirectUri = oauthRedirectUri();
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem('naver_state', state);
    window.location.href = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${AWS_CONFIG.naver.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
  };

  const handleKakaoLogin = () => {
    const redirectUri = oauthRedirectUri();
    const state = 'kakao_' + Math.random().toString(36).slice(2);
    sessionStorage.setItem('kakao_state', state);
    window.location.href = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${AWS_CONFIG.kakao.restApiKey}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
  };

  // Map Cognito/Amplify English error messages to friendly Korean copy.
  // SECURITY: NotAuthorizedException / UserNotFoundException / Incorrect username
  // are ALL collapsed into one generic "이메일·전화번호 또는 비밀번호가 올바르지
  // 않습니다" to prevent user-enumeration attacks. Backend also enforces this via
  // Cognito User Pool Client `PreventUserExistenceErrors=ENABLED` — this mapping
  // is the second layer in case Cognito ever leaks an exception in the future.
  const friendlyAuthError = (msg) => {
    const m = String(msg || '');
    if (!m) return '오류가 발생했습니다. 다시 시도해주세요.';
    if (m.includes('User already exists')) return '이미 가입된 이메일 또는 전화번호입니다. 비밀번호 분실 시 010-3931-4528로 문의해주세요.';
    if (m.includes('NotAuthorizedException') || m.includes('Incorrect username') || m.includes('UserNotFoundException')) return '이메일·전화번호 또는 비밀번호가 올바르지 않습니다';
    if (m.includes('InvalidPasswordException') || m.includes('Password did not conform') || m.includes('long enough') || m.includes('uppercase') || m.includes('lowercase') || m.includes('numeric')) return '비밀번호는 영문과 숫자를 포함해 8자 이상이어야 합니다';
    if (m.includes('Invalid phone number') || m.includes('phone_number')) return '전화번호 형식이 올바르지 않습니다 (예: 010-1234-5678)';
    if (m.includes('InvalidParameterException')) return '입력값이 올바르지 않습니다. 이름·전화번호·비밀번호를 다시 확인해주세요';
    if (m.includes('CodeMismatchException')) return '인증 코드가 일치하지 않습니다';
    if (m.includes('ExpiredCodeException')) return '인증 코드가 만료되었습니다. 다시 시도해주세요';
    if (m.includes('LimitExceededException') || m.includes('TooManyRequests')) return '시도 횟수를 초과했습니다. 잠시 후 다시 시도해주세요';
    return m;
  };

  const performSignUp = async () => {
    setLoading(true); setError('');
    try {
      const result = await signUp(form.email, form.password, form.name, form.phone);
      if (result.isSignUpComplete) {
        // AuthContext already signed the user in. Show a short success screen
        // before navigating so the user knows the signup actually succeeded
        // (without it the page just snaps to home, which feels uncertain).
        setStep('signup-done');
        setTimeout(() => navigate('/'), 1500);
      } else {
        setStep('confirm');
      }
    } catch (err) {
      setError(friendlyAuthError(err.message));
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (tab === 'register' && !form.email.trim()) {
      if (!form.name.trim() || !form.phone.trim() || !form.password) {
        setError('이름·전화번호·비밀번호를 모두 입력해주세요');
        return;
      }
      setPendingEmailLessSignUp(true);
      return;
    }
    setLoading(true); setError('');
    try {
      if (tab === 'login') {
        const result = await signIn(form.email, form.password);
        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') setStep('confirm');
        else if (result.isSignedIn) navigate('/');
      } else {
        await performSignUp();
        return;
      }
    } catch (err) {
      setError(friendlyAuthError(err.message));
    }
    setLoading(false);
  };

  if (user) {
    const lastProvider = (typeof window !== 'undefined' && localStorage.getItem('lastProvider')) || null;
    const providerLabel = { google: '구글', naver: '네이버', kakao: '카카오', email: '이메일' }[lastProvider];
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <span style={{ fontSize: '3rem' }}>👋</span><h3 style={{ marginTop: 16 }}>환영합니다!</h3>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          {providerLabel ? `${providerLabel} 계정 · ` : ''}{user.signInDetails?.loginId || user.username}
        </p>
        <button className="btn btn-outline" onClick={async () => { await signOut(); }} style={{ marginTop: 16 }}>로그아웃</button>
      </div>
    );
  }

  if (step === 'signup-done') return (
    <div style={{ maxWidth: 400, margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: 32, textAlign: 'center' }}>
      <span style={{ fontSize: '3rem' }}>✅</span>
      <h3 style={{ marginTop: 16, marginBottom: 8 }}>회원가입 완료!</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>잠시 후 홈으로 이동합니다...</p>
    </div>
  );

  if (step === 'confirm') return (
    <div style={{ maxWidth: 400, margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: 32 }}>
      <h3 style={{ marginBottom: 16 }}>이메일 인증</h3>
      <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: '0.9rem' }}>{form.email}로 전송된 코드를 입력해주세요</p>
      {error && <p style={{ color: '#ff4444', fontSize: '0.85rem', marginBottom: 12 }}>{error}</p>}
      <input className="input" placeholder="인증 코드 6자리" value={form.code} onChange={set('code')} style={{ marginBottom: 16 }} />
      <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading}
        onClick={async () => {
          setLoading(true); setError('');
          try { await confirmSignUp(form.email, form.code); await signIn(form.email, form.password); navigate('/'); }
          catch (err) { setError(friendlyAuthError(err.message)); }
          setLoading(false);
        }}>인증 완료</button>
    </div>
  );

  const socialBtnStyle = (bg, color, disabled) => ({
    width: '100%', padding: '12px', borderRadius: 'var(--radius)',
    background: disabled ? 'var(--bg)' : bg,
    color: disabled ? 'var(--text-dim)' : color,
    fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    border: disabled ? '1px solid var(--border)' : 'none',
    opacity: disabled ? 0.55 : 1,
    fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  });

  // Provider status drives the button enabled state and the warning text.
  // When a provider is disabled (e.g. Kakao during 비즈 검수) the button shows
  // its reason instead of an active CTA, so the user picks an alternative
  // without bouncing through a broken external auth screen.
  const googleStatus = providerStatus('google');
  const naverStatus  = providerStatus('naver');
  const kakaoStatus  = providerStatus('kakao');
  const SocialButton = ({ status, onClick, bg, color, logo, label }) => (
    <div>
      <button
        onClick={status.enabled ? onClick : undefined}
        disabled={!status.enabled}
        style={socialBtnStyle(bg, color, !status.enabled)}
      >
        {logo}
        {status.enabled ? label : `${label} (${status.reason || '점검 중'})`}
      </button>
      {status.enabled && status.warning && (
        <p style={{ margin: '4px 0 0 4px', fontSize: '0.75rem', color: 'var(--text-dim)' }}>
          ⓘ {status.warning}
        </p>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 400, margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ display: 'flex' }}>
        <button style={{ flex: 1, padding: '12px 0', borderBottom: tab==='login'?'2px solid var(--primary)':'none', color: tab==='login'?'var(--primary)':'var(--text-muted)', background: 'none' }} onClick={() => setTab('login')}>로그인</button>
        <button style={{ flex: 1, padding: '12px 0', borderBottom: tab==='register'?'2px solid var(--primary)':'none', color: tab==='register'?'var(--primary)':'var(--text-muted)', background: 'none' }} onClick={() => setTab('register')}>회원가입</button>
      </div>
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>소셜 계정으로 1초 만에 시작하기</p>
        <SocialButton status={googleStatus} onClick={handleGoogleLogin} bg="#4285F4" color="#fff" logo={<GoogleLogo />} label="구글로 시작하기" />
        <SocialButton status={naverStatus}  onClick={handleNaverLogin}  bg="#03C75A" color="#fff" logo={<NaverLogo />}  label="네이버로 시작하기" />
        <SocialButton status={kakaoStatus}  onClick={handleKakaoLogin}  bg="#FEE500" color="#3C1E1E" logo={<KakaoLogo />} label="카카오로 시작하기" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}><div style={{ flex: 1, height: 1, background: 'var(--border)' }} /><span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>또는</span><div style={{ flex: 1, height: 1, background: 'var(--border)' }} /></div>
        {tab === 'register' && (
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            💡 더 빠르고 안전한 가입은 ↑ 위 소셜 로그인을 추천합니다
          </div>
        )}
        {tab === 'register' && <input className="input" placeholder="이름" value={form.name} onChange={set('name')} />}
        <input className="input" placeholder={tab === 'register' ? '이메일 (선택, 권장)' : '이메일 또는 전화번호'} type={tab === 'register' ? 'email' : 'text'} value={form.email} onChange={set('email')} />
        <input className="input" placeholder="비밀번호" type="password" value={form.password} onChange={set('password')} />
        {tab === 'register' && <input className="input" placeholder="전화번호 (예: 010-1234-5678)" type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: formatKoreanPhone(e.target.value) }))} />}
        {error && (
          <div style={{
            background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 'var(--radius)',
            padding: '10px 12px', color: '#c62828', fontSize: '0.88rem', lineHeight: 1.5,
            whiteSpace: 'pre-line',
          }}>
            ⚠ {error}
          </div>
        )}
        <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>{tab === 'login' ? '로그인' : '회원가입'}</button>
      </div>
      {pendingPhoneVerification && (
        <PhoneVerificationModal open sub={pendingPhoneVerification.sub} provider={pendingPhoneVerification.provider}
          name={pendingPhoneVerification.name} email={pendingPhoneVerification.email} onSuccess={() => clearPendingPhoneVerification()}
          onCancel={() => clearPendingPhoneVerification()} />
      )}
      <ConfirmDialog
        open={pendingEmailLessSignUp}
        title="이메일 없이 가입하시겠습니까?"
        message={`이메일 없이 가입하면 다음 기능이 제한됩니다:\n\n• 비밀번호 분실 시 자동 복구 불가 — 관리자 문의 필요\n• 이메일 안내 메시지 미수신\n\n더 편리한 가입을 원하시면 [취소]를 누르고 위쪽 소셜 로그인을 이용해주세요.`}
        confirmLabel="그래도 가입"
        cancelLabel="취소"
        danger={true}
        onConfirm={async () => { setPendingEmailLessSignUp(false); await performSignUp(); }}
        onCancel={() => setPendingEmailLessSignUp(false)}
      />
    </div>
  );
}
